import { queryOptions, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import {
  AddIntEmbsRequest,
  FertAndEmbryosRequest,
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
  MassInterimEmbryosRequest,
  PdfCreateFreezeRequest,
  TempEmbryoResponse,
  UpdateApprovalNumberRequest,
  UpdateFieldRequest,
  UpdateFreezeEmbryosRequest,
  UpdateFreezeTempEmbryosRequest,
  UpdateODFreezeRequest,
  ValidateFreezeRequest,
} from './types';

export type {
  AddIntEmbsRequest,
  FertAndEmbryosRequest,
  FertAndEmbryosResponse,
  FertilizationListFreezeResponse,
  LiveEmbryoResponse,
  MassInterimEmbryosRequest,
  PdfCreateFreezeRequest,
  StageGradeCountRequest,
  TempEmbryoOwner,
  TempEmbryoResponse,
  UpdateApprovalNumberRequest,
  UpdateFieldRequest,
  UpdateFreezeEmbryosRequest,
  UpdateFreezeTempEmbryosRequest,
  UpdateMatingLabelsRequest,
  UpdateODFreezeRequest,
  ValidateFreezeRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

// ASP.NET Core's [ApiController] auto-400s on model-binding failures (e.g. an
// empty string posted to a Guid? property) and returns a ValidationProblemDetails
// body naming the offending field. Surface that instead of a bare status code so
// failures like "od-update" 400s are actionable from the toast/console alone.
async function readErrorDetail(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  if (!text) return '';

  try {
    const problem = JSON.parse(text) as {
      title?: string;
      detail?: string;
      errors?: Record<string, string[]>;
    };
    if (problem.errors) {
      const fieldErrors = Object.entries(problem.errors)
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join('; ');
      return fieldErrors || problem.title || problem.detail || text;
    }
    return problem.detail || problem.title || text;
  } catch {
    return text;
  }
}

async function throwHttpError(response: Response): Promise<never> {
  const detail = await readErrorDetail(response);
  throw new Error(
    detail
      ? `HTTP error! status: ${response.status}, message: ${detail}`
      : `HTTP error! status: ${response.status}`,
  );
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/freeze/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/freeze/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/freeze/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return (await response.json()) as T;
}

async function fetchMutationBlob(
  path: string,
  method: 'POST',
  body: unknown,
): Promise<Blob> {
  const url = `${getBaseUrl()}/api/v1/freeze/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await throwHttpError(response);
  }

  return response.blob();
}

async function fetchFertilizationListFreeze(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  fertilizationId?: string | null;
  searchQuery?: string | null;
  skip?: number | null;
  take?: number | null;
}): Promise<FertilizationListFreezeResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  if (params.fertilizationId)
    searchParams.set('fertilizationId', params.fertilizationId);
  if (params.searchQuery) searchParams.set('searchQuery', params.searchQuery);
  if (params.skip != null) searchParams.set('skip', params.skip.toString());
  if (params.take != null) searchParams.set('take', params.take.toString());
  return fetchGet<FertilizationListFreezeResponse[]>(
    'fertilizations',
    searchParams,
  );
}

export async function fetchFertAndEmbryos(
  params: FertAndEmbryosRequest,
): Promise<FertAndEmbryosResponse[]> {
  return fetchMutation<FertAndEmbryosResponse[]>(
    'fertilizations/fert-and-embryos',
    'POST',
    params,
  );
}

async function updateFertilizationField(
  id: string,
  input: UpdateFieldRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(`fertilizations/${id}`, 'PATCH', input);
}

async function updateEmbryoField(
  id: string,
  input: UpdateFieldRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(`embryos/${id}`, 'PATCH', input);
}

async function updateInterimEmbryoField(
  id: string,
  input: UpdateFieldRequest,
): Promise<boolean> {
  return fetchMutation<boolean>(`interim-embryos/${id}`, 'PATCH', input);
}

async function addMassInterimEmbryos(
  input: MassInterimEmbryosRequest,
  dtplus?: boolean,
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  if (dtplus) searchParams.set('dtplus', 'true');
  const path = searchParams.toString()
    ? `interim-embryos/mass?${searchParams.toString()}`
    : 'interim-embryos/mass';
  return fetchMutation<boolean>(path, 'POST', input);
}

async function addIntEmbs(
  input: AddIntEmbsRequest,
): Promise<TempEmbryoResponse[]> {
  return fetchMutation<TempEmbryoResponse[]>('interim-embryos', 'POST', input);
}

async function updateODFreeze(
  input: UpdateODFreezeRequest,
  dtplus?: boolean,
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  if (dtplus) searchParams.set('dtplus', 'true');
  const path = searchParams.toString()
    ? `od-update?${searchParams.toString()}`
    : 'od-update';
  return fetchMutation<boolean>(path, 'POST', input);
}

async function updateFreezeEmbryos(
  input: UpdateFreezeEmbryosRequest,
): Promise<boolean> {
  return fetchMutation<boolean>('embryos', 'PUT', input);
}

async function updateFreezeTempEmbryos(
  input: UpdateFreezeTempEmbryosRequest,
): Promise<boolean> {
  return fetchMutation<boolean>('temp-embryos', 'PUT', input);
}

async function deactivateInterimEmbryos(ids: string[]): Promise<boolean> {
  return fetchMutation<boolean>('interim-embryos', 'DELETE', { ids });
}

async function createFreezePdf(input: PdfCreateFreezeRequest): Promise<Blob> {
  return fetchMutationBlob('pdf', 'POST', input);
}

async function validateFreeze(input: ValidateFreezeRequest): Promise<boolean> {
  return fetchMutation<boolean>('validate', 'POST', input);
}

async function applyApprovalNumbers(
  updates: UpdateApprovalNumberRequest[],
): Promise<boolean> {
  return fetchMutation<boolean>('approval-numbers', 'POST', updates);
}

export const fertilizationListFreezeOptions = (params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  fertilizationId?: string | null;
  searchQuery?: string | null;
  skip?: number | null;
  take?: number | null;
}) =>
  queryOptions({
    queryKey: ['freeze', 'fertilizations', params],
    async queryFn() {
      return fetchFertilizationListFreeze(params);
    },
  });

export const fertAndEmbryosOptions = (params: FertAndEmbryosRequest) =>
  queryOptions({
    queryKey: ['freeze', 'fert-and-embryos', params],
    async queryFn() {
      return fetchFertAndEmbryos(params);
    },
  });

export function useUpdateFertilizationFieldMutation() {
  return useMutation({
    mutationFn: (params: { id: string; field: string; value: string | null }) =>
      updateFertilizationField(params.id, {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useUpdateEmbryoFieldMutation() {
  return useMutation({
    mutationFn: (params: { id: string; field: string; value: string | null }) =>
      updateEmbryoField(params.id, {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useUpdateInterimEmbryoFieldMutation() {
  return useMutation({
    mutationFn: (params: { id: string; field: string; value: string | null }) =>
      updateInterimEmbryoField(params.id, {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useAddMassInterimEmbryosMutation() {
  return useMutation({
    mutationFn: (params: {
      input: MassInterimEmbryosRequest;
      dtplus?: boolean;
    }) => addMassInterimEmbryos(params.input, params.dtplus),
  });
}

export function useAddIntEmbsMutation() {
  return useMutation({
    mutationFn: (params: AddIntEmbsRequest) => addIntEmbs(params),
  });
}

export function useUpdateODFreezeMutation() {
  return useMutation({
    mutationFn: (params: { input: UpdateODFreezeRequest; dtplus?: boolean }) =>
      updateODFreeze(params.input, params.dtplus),
  });
}

export function useUpdateFreezeEmbryosMutation() {
  return useMutation({
    mutationFn: (input: UpdateFreezeEmbryosRequest) =>
      updateFreezeEmbryos(input),
  });
}

export function useUpdateFreezeTempEmbryosMutation() {
  return useMutation({
    mutationFn: (input: UpdateFreezeTempEmbryosRequest) =>
      updateFreezeTempEmbryos(input),
  });
}

export function useDeactivateInterimEmbryosMutation() {
  return useMutation({
    mutationFn: (ids: string[]) => deactivateInterimEmbryos(ids),
  });
}

export function useCreateFreezePdfMutation() {
  return useMutation({
    mutationFn: (input: PdfCreateFreezeRequest) => createFreezePdf(input),
  });
}

export function useValidateFreezeMutation() {
  return useMutation({
    mutationFn: (input: ValidateFreezeRequest) => validateFreeze(input),
  });
}

export function useApplyApprovalNumbersMutation() {
  return useMutation({
    mutationFn: (updates: UpdateApprovalNumberRequest[]) =>
      applyApprovalNumbers(updates),
  });
}
